import { useEffect, useState, useMemo, useContext, useReducer, useRef, useCallback, lazy, Suspense } from "react";
import { message, Space, Flex, Tag, Breadcrumb, Button, Modal, Popover, Divider, Card, Typography, InputNumber } from "antd";
import {
  BankOutlined as OrganizationIcon,
  EnvironmentOutlined as SiteIcon,
  SettingOutlined,
} from "@ant-design/icons";

import _ from "lodash";
import { Footer, LoadingIcon, Sidebar } from "../../../components/index.js";
import { UserContext } from "../../../context/UserContext.jsx";
const WeeklySummaryBar = lazy(() => import("./WeeklySummaryBar.jsx").then((m) => ({ default: m.WeeklySummaryBar })));
import { HarvestContext, HarvestUIContext } from "./HarvestContext.js";
const HarvestViewer = lazy(() => import("./HarvestViewer.jsx").then((m) => ({ default: m.HarvestViewer })));
import { HarvestSetup } from "./HarvestSetup.jsx";
import { SpecSize } from "./SpecSize.jsx";
import { reducer } from "./reducer.js";
import {
  getOptimisedHarvestPlan,
  fetchDataFromSupabase,
  fetchSiteConfigFromSupabase,
} from "./harvestOptimiser.service.js";
import { updateSiteMetadata } from "../../../utils/api.js";
import { getSiteTimezone } from "./siteTimezone.js";
import { cmToInches } from "../../../utils/cmToInchesConversion.js";
// import { demoDateFix } from "./harvestOptimiser.utils.js";


export default function BroccoliHarvestOptimiser({
  currentOrg,
  setCurrentOrg,
  orgData,
  siteData,
  currentSite,
  setCurrentSite,
  currentCrop,
  setCurrentCrop,
  cropData,
}) {
  const { userData } = useContext(UserContext);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [lookupTable, setLookupTable] = useState(null);
  const [actualYieldTable, setActualYieldTable] = useState(null);
  const [recommendedHarvestPlan, setRecommendedHarvestPlan] = useState(null);
  const [siteConfig, setSiteConfig] = useState(null);

  const [harvestState, harvestDispatch] = useReducer(reducer, null);
  const [loading, setLoading] = useState(false);

  const [selectedDay, setSelectedDay] = useState("ALL");
  const [isForecastAvailable, setIsForecastAvailable] = useState(true);
  const [activeWeek, setActiveWeek] = useState("current");
  const preserveTotalCardRef = useRef(false);

  const setWeekTotal = useCallback((targetWeek) => {
    preserveTotalCardRef.current = true;
    setActiveWeek(targetWeek);
    setSelectedDay("ALL");
  }, []);
  const [setupOpen, setSetupOpen] = useState(false);
  const [configPopoverOpen, setConfigPopoverOpen] = useState(false);
  const [kgPerBin, setKgPerBin] = useState(
    () => currentSite?.metadata?.broccoli?.OPTIMISER?.config?.weigh_mode?.bin_kg_per_unit ?? null
  );
  const [kgPerBinUpdated, setKgPerBinUpdated] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [specSizeUpdated, setSpecSizeUpdated] = useState(false);
  const specSizeRef = useRef(null);
  const [yieldUnit, setYieldUnit] = useState(() => {
    const displayUnits = currentSite?.metadata?.siteConfig?.display_units;
    return displayUnits === "imperial" ? "lb" : "ton";
  });
  const [areaUnit, setAreaUnit] = useState(() => {
    const areaUnitSetting = currentSite?.metadata?.siteConfig?.units?.area;
    return areaUnitSetting === "acre" ? "acre" : "Hectare";
  });
  const isImperial = currentSite?.metadata?.siteConfig?.display_units === "imperial";

  const handleKgPerBinChange = (value) => {
    setKgPerBin(value);
    setKgPerBinUpdated(true);
  };

  useEffect(() => {
    setKgPerBin(currentSite?.metadata?.broccoli?.OPTIMISER?.config?.weigh_mode?.bin_kg_per_unit ?? null);
  }, [currentSite]);

  useEffect(() => {
    const areaUnitSetting = currentSite?.metadata?.siteConfig?.units?.area;
    setAreaUnit(areaUnitSetting === "acre" ? "acre" : "Hectare");
  }, [currentSite]);

  useEffect(() => {
    const displayUnits = currentSite?.metadata?.siteConfig?.display_units;
    setYieldUnit(displayUnits === "imperial" ? "lb" : "ton");
  }, [currentSite?.metadata?.siteConfig?.display_units]);

  const handleConfigSave = async () => {
    setConfigSaving(true);
    try {
      if (specSizeUpdated && specSizeRef.current) {
        await specSizeRef.current.save();
        setSpecSizeUpdated(false);
      }

      if (kgPerBinUpdated && kgPerBin !== null) {
        const siteMetaData = currentSite.metadata ?? {};
        const updatedMetadata = {
          ...siteMetaData,
          broccoli: {
            ...siteMetaData?.broccoli,
            OPTIMISER: {
              ...siteMetaData?.broccoli?.OPTIMISER,
              config: {
                ...siteMetaData?.broccoli?.OPTIMISER?.config,
                weigh_mode: {
                  ...siteMetaData?.broccoli?.OPTIMISER?.config?.weigh_mode,
                  bin_kg_per_unit: kgPerBin,
                },
              },
            },
          },
        };
        await updateSiteMetadata(currentSite.id, { metadata: updatedMetadata });
        setKgPerBinUpdated(false);
      }
      setConfigPopoverOpen(false);
    } catch (error) {
      message.error('Failed to save configuration');
      console.error(error);
    } finally {
      setConfigSaving(false);
    }
  };

  const isTabletPortrait = () => {
    const isTouch = navigator.maxTouchPoints > 0;
    const width = window.innerWidth;
    const height = window.innerHeight;
    return isTouch && width < 1024 && height > width;
  };
  const [showOrientationModal, setShowOrientationModal] = useState(isTabletPortrait);

  useEffect(() => {
    const handleChange = () => setShowOrientationModal(isTabletPortrait());
    window.addEventListener("resize", handleChange);
    window.addEventListener("orientationchange", handleChange);
    screen.orientation?.addEventListener("change", handleChange);
    return () => {
      window.removeEventListener("resize", handleChange);
      window.removeEventListener("orientationchange", handleChange);
      screen.orientation?.removeEventListener("change", handleChange);
    };
  }, []);
  const currentOrgName = _.find(orgData, { id: currentOrg })?.name;

  const getCropDisplayName = (cropName) => {
    if (!cropName) return "";
    const lowerName = cropName.toLowerCase();
    if (lowerName.includes("broccoli")) return `🥦 ${cropName}`;
    if (lowerName.includes("spinach")) return `🌱 ${cropName}`;
    return cropName;
  };

  const breadcrumbsItems = [
    ...(userData.is_superuser ? [{
      title: (
        <Tag color={"gold"}>
          <OrganizationIcon /> {currentOrgName}
        </Tag>
      ),
      menu: {
        items: orgData?.map((org) => {
          return { key: org.id, label: org.name };
        }),
        onClick: (e) => setCurrentOrg(e.key),
      },
    }] : []),
    {
      title: (
        <Tag color={"blue"}>
          <SiteIcon /> {currentSite?.displayName}
        </Tag>
      ),
      menu: {
        items: siteData?.map((site) => ({ key: site.id, label: site.displayName })),
        onClick: (e) => {
          const siteObj = siteData.find((s) => s.id === e.key);
          setCurrentSite(siteObj);
        },
      },
    },
    {
      title: <Tag color={"green"}>{currentCrop?.displayName}</Tag>,
      menu: {
        items: cropData?.map((crop) => ({
          key: crop.id,
          label: getCropDisplayName(crop.displayName),
        })),
        onClick: (e) => {
          const cropObj = cropData.find((c) => c.id === e.key);
          setCurrentCrop(cropObj);
        },
      },
    },
  ];

  useEffect(() => {
    if (!currentSite?.name) return;

    const controller = new AbortController();

    const fetchHarvestOptimiserData = async () => {
      setLoading(true);
      try {
        const [supabaseData, harvestPlanRows] = await Promise.all([
          fetchDataFromSupabase(currentSite.name),
          getOptimisedHarvestPlan(currentOrg, currentSite.id),
        ]);
        if (controller.signal.aborted) return;

        const actualYieldByKey = _.keyBy(
          supabaseData.actualYieldTable,
          (r) => `${r.paddock}__${r.block}__${r.pass_number}`,
        );
        const standCountByKey = _.keyBy(supabaseData.configTable, (r) => `${r.paddock}__${r.block}`);
        const enrichedPlan = harvestPlanRows.map((row) => ({
          ...row,
          actual_yield_pass1: actualYieldByKey[`${row.paddock}__${row.block}__1`]?.actual_yield ?? null,
          actual_yield_pass2: actualYieldByKey[`${row.paddock}__${row.block}__2`]?.actual_yield ?? null,
          total_stand_count: standCountByKey[`${row.paddock}__${row.block}`]?.total_stand_count ?? null,
        }));

        setLookupTable(supabaseData.lookupTable);
        setActualYieldTable(supabaseData.actualYieldTable);
        setRecommendedHarvestPlan(enrichedPlan);
      } catch {
        if (!controller.signal.aborted) message.error("Failed to load harvest forecast data.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    const getSiteConfig = async () => {
      try {
        const { siteConfig } = await fetchSiteConfigFromSupabase(currentSite.name);
        if (controller.signal.aborted) return;
        setSiteConfig(siteConfig);
      } catch {
        if (!controller.signal.aborted) message.error("Failed to load site config data.");
      }
    };

    fetchHarvestOptimiserData();
    getSiteConfig();

    return () => controller.abort();
  }, [currentSite?.id, currentSite?.name, currentOrg]);

  useEffect(() => {
    if (!recommendedHarvestPlan) return;
    harvestDispatch({
      type: "INITIALIZE",
      payload: {
        currentHarvestPlan: recommendedHarvestPlan,
        recommendedHarvestPlan,
      },
    });
  }, [recommendedHarvestPlan]);

  const contextValue = useMemo(
    () => ({
      currentOrg,
      currentSite,
      lookupTable,
      actualYieldTable,
      recommendedHarvestPlan,
      setRecommendedHarvestPlan,
      siteConfig,
      siteMetaData: currentSite.metadata ?? {},
      siteTimezone: getSiteTimezone(siteConfig, currentSite),
    }),
    [currentOrg, currentSite, lookupTable, actualYieldTable, recommendedHarvestPlan, siteConfig],
  );


  return (
    <>
      <Modal
        open={showOrientationModal}
        footer={null}
        closable={false}
        centered
        title="Rotate Your Device"
      >
        <Flex vertical align="center" gap={16} style={{ padding: "16px 0" }}>
          <span style={{ fontSize: 48 }}>🔄</span>
          <p style={{ textAlign: "center", margin: 0 }}>
            Please rotate your device to <strong>landscape mode</strong> for the best experience.
          </p>
        </Flex>
      </Modal>

      <div id="dashboard">
        <Sidebar
          active="broccoli_harvest_optimizer"
          sidebarType="BroccoliGrower"
          open={sidebarOpen}
          setOpen={setSidebarOpen}
        />

        <HarvestContext.Provider value={contextValue}>
          <HarvestUIContext.Provider value={{ selectedDay, setSelectedDay, isForecastAvailable, setIsForecastAvailable, activeWeek, setActiveWeek, setWeekTotal, preserveTotalCardRef, yieldUnit, setYieldUnit, kgPerBin, areaUnit, setAreaUnit, displayUnits: currentSite?.metadata?.siteConfig?.display_units }}>
            <HarvestSetup open={setupOpen} onOpen={setSetupOpen} />

            <div
              className={`flex flex-col w-full px-20 lg:px-6 ${
                sidebarOpen ? "pl-60 sm:pl-10" : "sm:pl-14"
              } pt-10 md:pt-4 pb-14 md:pl-15 md:pr-2 sm:pr-4 justify-center transition-padding ease-in-out duration-200`}
            >
              <Flex justify="space-between" align="center" style={{ width: '100%', position: 'relative' }}>
                <Space size="large">
                  <Breadcrumb items={breadcrumbsItems} />

                  <Button
                    color="primary"
                    variant="filled"
                    onClick={() => setSetupOpen(true)}
                  >
                    Harvest Setup
                  </Button>
                </Space>

                <Flex justify="center" align="center" style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
                  {siteConfig?.minimum_spec_size != null && siteConfig?.maximum_spec_size != null && (
                    <Tag color="purple">
                      <strong>Current Spec Size:</strong> {isImperial ? cmToInches(siteConfig.minimum_spec_size).toFixed(2) : siteConfig.minimum_spec_size} {isImperial ? "in" : "cm"} - {isImperial ? cmToInches(siteConfig.maximum_spec_size).toFixed(2) : siteConfig.maximum_spec_size} {isImperial ? "in" : "cm"}
                    </Tag>
                  )}
                </Flex>

                <Popover
                  content={
                    <div style={{ width: '380px' }}>
                      <SpecSize
                        ref={specSizeRef}
                        onSaveComplete={() => setConfigPopoverOpen(false)}
                        showFootnote={true}
                        hideButton={true}
                        onSpecSizeChange={() => setSpecSizeUpdated(true)}
                      />
                      <Divider />
                      <Card title="Weight configuration" size="small">
                        <Space vertical style={{ width: '100%' }}>
                          <Flex vertical gap={8}>
                            <Typography.Text className="text-sm">
                              {currentSite?.metadata?.siteConfig?.display_units === "imperial" ? "Pounds per bin" : "Kg per bin"}
                            </Typography.Text>
                            <InputNumber
                              value={kgPerBin}
                              className="w-full"
                              mode="spinner"
                              onChange={handleKgPerBinChange}
                              min={1}
                              step={1}
                              placeholder={currentSite?.metadata?.siteConfig?.display_units === "imperial" ? "Enter pounds per bin" : "Enter kg per bin"}
                            />
                          </Flex>
                        </Space>
                      </Card>
                      <Divider />
                      <Flex justify="flex-end" gap={8}>
                        <Button onClick={() => setConfigPopoverOpen(false)}>Cancel</Button>
                        <Button
                          type="primary"
                          onClick={handleConfigSave}
                          loading={configSaving}
                        >
                          Save
                        </Button>
                      </Flex>
                    </div>
                  }
                  trigger="click"
                  placement="bottomLeft"
                  open={configPopoverOpen}
                  onOpenChange={setConfigPopoverOpen}
                >
                  <Button
                    type="text"
                    aria-label="Open configuration settings"
                    icon={<SettingOutlined style={{ color: '#1890ff', fontSize: '20px' }} />}
                  />
                </Popover>
              </Flex>

              <div className="harvest-container">
                {loading ? (
                  <LoadingIcon height="200px" classname="mt-6" />
                ) : (
                  <Suspense fallback={<LoadingIcon height="200px" classname="mt-6" />}>
                    <div className="mt-10">
                      <WeeklySummaryBar recommendedHarvestPlan={harvestState?.currentHarvestPlan ?? recommendedHarvestPlan} actualYieldTable={actualYieldTable} />
                    </div>
                    <div className="mt-10">
                      <HarvestViewer state={harvestState} dispatch={harvestDispatch} />
                    </div>
                  </Suspense>
                )}
              </div>

              
            </div>
          </HarvestUIContext.Provider>
        </HarvestContext.Provider>
        <Footer sidebarOpen={sidebarOpen} />
      </div>
    </>
  );
}
